import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import * as I from 'immutable';
import moment from 'moment';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import {useAlertPoliciesForFeatureCollections} from 'app/components/Alerts/AlertPolicyProvider';
import {alertPolicyUsesOverlay} from 'app/components/Alerts/alertPolicyUtils';
import CenteredSpinner from 'app/components/CenteredSpinner';
import {AlertPolicy} from 'app/modules/Remote/Feature';
import {ApiOrganization, ApiOrganizationUser, ApiOverlay} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import {useProjects} from 'app/providers/ProjectsProvider';
import {recordEvent} from 'app/tools/Analytics';
import {getPrimaryFeatureCollectionIds} from 'app/utils/featureCollectionUtils';
import * as featureUtils from 'app/utils/featureUtils';
import {overlayIsReady} from 'app/utils/overlayUtils';
import {useCurrentOrgIdPrefix} from 'app/utils/routeUtils';

import cs from './LensLibraryView.module.scss';
import {LibraryDatasetType} from './LibraryTypes';
import {useOverlays} from './OverlaysProvider';
import {LibraryItemDropdown} from './portfolioMenu';

const OverlaysView: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    role: ApiOrganizationUser['role'];
  }>
> = ({organization, role}) => {
  const {actions, overlays} = useOverlays();
  const [projectsMap, featureCollections, , projectsMeta] = useProjects();

  const isLoading = projectsMeta.loading || !overlays.size;

  const projects = (projectsMap || I.OrderedMap()).valueSeq().toList();
  const primaryFeatureCollectionIds = React.useMemo(
    () => getPrimaryFeatureCollectionIds(projectsMap, featureCollections),
    [projectsMap, featureCollections]
  );
  const {data: alertPolicies} = useAlertPoliciesForFeatureCollections(primaryFeatureCollectionIds);

  const addLibraryItem = (
    projectIds: string[],
    featureCollectionId: string,
    addTracking: boolean,
    name: string
  ): Promise<void> => {
    if (addTracking) {
      recordEvent('Added Upstream overlay', {
        name: name,
        portfolioCount: projectIds.length,
      });
    }
    return actions.addOverlays(projectIds, parseInt(featureCollectionId));
  };

  const removeLibraryItem = (
    projectIds: string[],
    featureCollectionId: string,
    addTracking = false,
    name: string
  ): Promise<void> => {
    if (addTracking) {
      recordEvent('Removed Upstream overlay', {
        name: name,
        portfolioCount: projectIds.length,
      });
    }
    return actions.removeOverlays(projectIds, parseInt(featureCollectionId));
  };

  const deleteLibraryItem = (featureCollectionId: string): Promise<void> =>
    actions.deleteOverlay(parseInt(featureCollectionId));

  const downloadLibraryItem = (featureCollectionId: string): Promise<void> => {
    return actions.downloadOverlay(parseInt(featureCollectionId));
  };

  const renameLibraryItem = (featureCollectionId: string, name: string): Promise<void> =>
    actions.updateOverlay(parseInt(featureCollectionId), name);

  const userUploadedOverlays = React.useMemo(() => {
    return overlays
      .filter((o) => !!o && o.get('uploadedBy') === organization.get('name'))
      .sortBy((o) => o!.get('name'), featureUtils.alphanumericSort)
      .toArray();
  }, [overlays, organization]);

  return isLoading ? (
    <CenteredSpinner />
  ) : (
    <CustomOverlaysTable
      overlays={userUploadedOverlays}
      alertPolicies={alertPolicies || []}
      projects={projects}
      role={role}
      addLibraryItem={addLibraryItem}
      removeLibraryItem={removeLibraryItem}
      deleteLibraryItem={deleteLibraryItem}
      downloadLibaryItem={downloadLibraryItem}
      renameLibraryItem={renameLibraryItem}
    />
  );
};

const CustomOverlaysTable: React.FunctionComponent<
  React.PropsWithChildren<{
    overlays: I.ImmutableOf<ApiOverlay>[];
    projects: I.ImmutableListOf<ApiProject>;
    alertPolicies: AlertPolicy[];
    role: ApiOrganizationUser['role'];
    addLibraryItem: (
      projectIds: string[],
      keyName: string,
      addTracking: boolean,
      name: string,
      type: LibraryDatasetType
    ) => Promise<void>;
    removeLibraryItem: (
      projectIds: string[],
      keyName: string,
      addTracking: boolean,
      name: string,
      type: LibraryDatasetType
    ) => Promise<void>;
    deleteLibraryItem: (keyName: string) => Promise<void>;
    downloadLibaryItem: (keyName: string) => Promise<void>;
    renameLibraryItem: (keyName: string, name: string) => Promise<void>;
  }>
> = ({
  overlays,
  projects,
  alertPolicies,
  role,
  addLibraryItem,
  deleteLibraryItem,
  downloadLibaryItem,
  removeLibraryItem,
  renameLibraryItem,
}) => {
  const isReadOnly = role === 'readonly';
  const navigate = useNavigate();
  const orgIdPrefix = useCurrentOrgIdPrefix();
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [editingName, setEditingName] = React.useState('');

  const startEdit = (featureCollectionId: number, currentName: string) => {
    setEditingId(featureCollectionId);
    setEditingName(currentName);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const saveEdit = async (featureCollectionId: number) => {
    const trimmed = editingName.trim();
    if (!trimmed) return;
    await renameLibraryItem(featureCollectionId.toString(), trimmed);
    cancelEdit();
  };

  return !overlays.length ? (
    <div className={cs.overlaysBlankstate}>
      <B.NonIdealState
        iconMuted={false}
        icon={<B.Icon icon="new-layers" size={120} />}
        description={
          <>
            Add a{' '}
            <a href="https://lens.tech/support/article/34-adding-overlays#adding">
              custom overlay
            </a>{' '}
            to view your vector data in Lens, such as building envelopes, photopoints, trails, and
            more.
          </>
        }
        action={
          <B.AnchorButton
            intent="primary"
            text={'Add an overlay'}
            icon={'plus'}
            onClick={() => navigate(`/${orgIdPrefix}/admin/manageProperties/overlay`)}
          />
        }
      ></B.NonIdealState>
    </div>
  ) : (
    <div className={cs.tableWrapper}>
      <div className={cs.tableHeader}>
        <h2>Custom Overlays</h2>
        <B.AnchorButton
          intent="primary"
          text={'Add an overlay'}
          icon={'plus'}
          onClick={() => navigate(`/${orgIdPrefix}/admin/manageProperties/overlay`)}
        />
      </div>

      <table className={cs.table}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Created On</th>
            <th>Usage</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          {overlays.map((o: I.ImmutableOf<ApiOverlay>, i) => {
            const alertPoliciesForOverlay = alertPolicies.filter((ap) =>
              alertPolicyUsesOverlay(ap, o.get('featureCollectionId'))
            );
            const isProcessing = !overlayIsReady(o.get('status'));
            const name = o.get('name');
            const fcId = o.get('featureCollectionId');
            return (
              <tr key={i}>
                <td className={classnames(cs.nameCell, cs.tableCell)}>
                  {editingId === fcId ? (
                    <div className={cs.nameEditControls}>
                      <B.InputGroup
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            saveEdit(fcId);
                          }
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        fill
                        autoFocus
                      />
                      <div className={cs.nameEditActions}>
                        <B.AnchorButton
                          icon={'tick'}
                          minimal
                          aria-label="save"
                          disabled={!editingName.trim()}
                          onClick={() => saveEdit(fcId)}
                        />
                        <B.AnchorButton
                          icon={'cross'}
                          minimal
                          aria-label="cancel"
                          onClick={cancelEdit}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className={cs.nameCellContent}>
                      <span>
                        {name}
                        {isProcessing && (
                          <B.Tooltip content={'Overlay is processing'} position="top">
                            <B.Icon
                              icon="time"
                              intent="warning"
                              className={cs.overlayProcessingIcon}
                            />
                          </B.Tooltip>
                        )}
                      </span>
                      {!isReadOnly && (
                        <B.AnchorButton
                          icon={'edit'}
                          minimal
                          aria-label="edit"
                          className={cs.nameEditBtn}
                          onClick={() => startEdit(fcId, name)}
                        />
                      )}
                    </div>
                  )}
                </td>
                <td className={cs.tableCell}>
                  {moment(o.get('createdAt')).format('MMMM D, YYYY')}
                </td>
                <td className={cs.tableCell}>
                  <LibraryItemDropdown
                    name={o.get('name')}
                    keyName={o.get('featureCollectionId').toString()}
                    enrolledProjectIds={o.get('projectIds').toJS()}
                    addTracking={o.get('uploadedBy') === 'Upstream System'}
                    projects={projects}
                    disabled={false}
                    addLibraryItem={addLibraryItem}
                    removeLibraryItem={removeLibraryItem}
                    type={LibraryDatasetType.OVERLAY}
                  >
                    <B.Button
                      className={cs.usageCell}
                      rightIcon="caret-down"
                      minimal
                      text={
                        o.get('projectIds').size === 1
                          ? `1 portfolio`
                          : `${o!.get('projectIds').size} portfolios`
                      }
                    />
                  </LibraryItemDropdown>
                </td>
                <td className={cs.deleteCell}>
                  <B.Tooltip content="Download overlay" minimal>
                    <B.AnchorButton
                      icon={'download'}
                      minimal
                      aria-label="download"
                      onClick={() => downloadLibaryItem(fcId.toString())}
                    />
                  </B.Tooltip>
                  {!isReadOnly && (
                    <B.Tooltip
                      content={
                        isProcessing
                          ? 'Overlay cannot be updated while processing'
                          : 'Update overlay'
                      }
                      minimal
                    >
                      <B.AnchorButton
                        icon={'reset'}
                        minimal
                        aria-label="update"
                        disabled={isProcessing}
                        onClick={() =>
                          navigate(`/${orgIdPrefix}/admin/manageProperties/updateOverlay/${fcId}`)
                        }
                      />
                    </B.Tooltip>
                  )}
                  <B.Tooltip content="Delete overlay" minimal>
                    <B.AnchorButton
                      icon={'trash'}
                      minimal
                      aria-label="delete"
                      onClick={() => {
                        const confirmText = `Are you sure you want to delete ${name} permanently from your library? You will no longer be able to add or remove it from your portfolios.
                      ${alertPoliciesForOverlay.length > 0 ? `\n\nThis will affect ${alertPoliciesForOverlay.length} lookout(s) using this overlay.` : ''}`;

                        if (confirm(confirmText)) {
                          deleteLibraryItem(fcId.toString());
                        }
                      }}
                    />
                  </B.Tooltip>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default OverlaysView;
